package br.com.htms.inspeccaocampo.shared.ui.components

import br.com.htms.inspeccaocampo.ui.kit.LinhaOpcoes
import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import br.com.htms.inspeccaocampo.shared.ui.components.AppOutlinedTextField
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.FotoAnexo
import br.com.htms.inspeccaocampo.data.ItemConformidade
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.data.TipoCampoExtra

enum class RotuloConformidade {
    C_NC_NA,
    SIM_NAO_NA,
    CONFORME
}

@Composable
fun ItemConformidadeCard(
    item: ItemConformidade,
    erro: String?,
    onResposta: (RespostaConformidade) -> Unit,
    onJustificativa: (String) -> Unit,
    onValorExtra: (String) -> Unit = {},
    onTirarFoto: (() -> Unit)? = null,
    onAdicionarMaisFoto: (() -> Unit)? = null,
    onRemoverFoto: ((FotoAnexo) -> Unit)? = null,
    onGaleria: (() -> Unit)? = null,
    rotulos: RotuloConformidade = RotuloConformidade.C_NC_NA,
    justificativaSomenteNa: Boolean = false,
    modifier: Modifier = Modifier
) {
    var mostrarLista by remember { mutableStateOf(false) }
    val exigeJustificativa = if (justificativaSomenteNa) {
        item.resposta == RespostaConformidade.NA
    } else {
        item.resposta == RespostaConformidade.NC || item.resposta == RespostaConformidade.NA
    }
    val mostrarFotos = item.fotoHabilitada && item.resposta != null &&
        item.resposta != RespostaConformidade.NA

    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface), border = PortalCores.BordaCartao
    ) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(item.tituloNumerado, fontWeight = FontWeight.SemiBold, color = PortalCores.Navy)

            LinhaOpcoes(
                opcoes = RespostaConformidade.entries,
                selecionada = item.resposta,
                rotulo = { opcao ->
                    when (rotulos) {
                        RotuloConformidade.C_NC_NA -> opcao.codigo
                        RotuloConformidade.SIM_NAO_NA -> opcao.rotuloSimNao
                        RotuloConformidade.CONFORME -> when (opcao) {
                            RespostaConformidade.C -> "Conforme"
                            RespostaConformidade.NC -> "Não conforme"
                            RespostaConformidade.NA -> "N/A"
                        }
                    }
                },
                cor = { opcao ->
                    when (opcao) {
                        RespostaConformidade.C -> PortalCores.Verde
                        RespostaConformidade.NC -> PortalCores.Vermelho
                        RespostaConformidade.NA -> PortalCores.CinzaTexto
                    }
                },
                onSelect = onResposta
            )

            if (item.tipoExtra != TipoCampoExtra.NENHUM && item.resposta != null) {
                AppOutlinedTextField(
                    value = item.valorExtra,
                    onValueChange = { raw ->
                        onValorExtra(
                            if (item.tipoExtra == TipoCampoExtra.NUMERO) {
                                raw.filter { it.isDigit() || it == ',' || it == '.' }
                            } else raw
                        )
                    },
                    label = { Text(item.labelExtra.ifBlank { "Valor" }) },
                    modifier = Modifier.fillMaxWidth(),
                    keyboardOptions = KeyboardOptions(
                        keyboardType = if (item.tipoExtra == TipoCampoExtra.NUMERO) {
                            KeyboardType.Decimal
                        } else {
                            KeyboardType.Text
                        }
                    ),
                    colors = FormFieldDefaults.textFieldColors()
                )
            }

            if (exigeJustificativa) {
                AppOutlinedTextField(
                    value = item.justificativa,
                    onValueChange = onJustificativa,
                    label = { Text("Justificativa *") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 2,
                    colors = FormFieldDefaults.textFieldColors(),
                    isError = erro != null
                )
            }

            if (mostrarFotos && onTirarFoto != null && onAdicionarMaisFoto != null) {
                CampoFotoRow(
                    label = "Câmera",
                    obrigatorio = item.fotoObrigatoria || item.resposta == RespostaConformidade.NC,
                    quantidadeFotos = item.fotos.size,
                    onTirarFoto = onTirarFoto,
                    onAdicionarMais = onAdicionarMaisFoto,
                    onAbrirLista = { mostrarLista = true },
                    onGaleria = onGaleria
                )
            }

            erro?.let {
                Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
            }
        }
    }

    if (mostrarLista && onRemoverFoto != null) {
        SharedFotosMenu(
            titulo = item.tituloNumerado,
            fotos = item.fotos,
            aberto = true,
            onFechar = { mostrarLista = false },
            onRemover = onRemoverFoto
        )
    }
}

@Composable
fun LegendaConformidade(
    modifier: Modifier = Modifier,
    rotulos: RotuloConformidade = RotuloConformidade.C_NC_NA
) {
    Text(
        when (rotulos) {
            RotuloConformidade.C_NC_NA -> RespostaConformidade.LEGENDA
            RotuloConformidade.SIM_NAO_NA -> RespostaConformidade.LEGENDA_SIM_NAO
            RotuloConformidade.CONFORME -> "Conforme pede foto. Não conforme pede foto. N/A pede justificativa."
        },
        modifier = modifier.fillMaxWidth(),
        style = MaterialTheme.typography.bodySmall,
        fontWeight = FontWeight.SemiBold,
        color = MaterialTheme.colorScheme.primary
    )
}
